import { investmentProductRow } from './investmentProduct';
import type { JSONContent } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { PricingType } from '@/theme/uboTheme';
import { formatCurrency } from '@/lib/format';
import type { SavedInvestmentTable } from '@/lib/investmentTableLibrary';
import {
  buildInvestmentHeaderRowJson,
  type InvestmentTableLabelsResolved,
} from '@/editor/investmentTableLabels';
import { investmentTitleCellContent } from '@/editor/investmentTableCellContent';
import { applyInvestmentColumnWidths } from '@/editor/investmentColumnWidths';

/** "Titel (kopie)", "Titel (kopie 2)", …: elke kopie is in de lijst en de offerte te herkennen. */
export function investmentCopyTitle(title: string, existingTitles: Iterable<string> = []): string {
  const base =
    title.trim().replace(/(?:\s*\(kopie(?: \d+)?\)|\s+kopie)$/i, '') || 'Investeringsoverzicht';
  const taken = new Set(existingTitles);
  let copy = `${base} (kopie)`;
  for (let index = 2; taken.has(copy); index++) copy = `${base} (kopie ${index})`;
  return copy;
}

export function cloneInvestmentPhaseNodes(
  nodes: JSONContent[],
  newTableId: string,
  existingTitles: Iterable<string> = []
): JSONContent[] {
  return nodes.map((node) => {
    if (node.type === 'investmentHeaderNode') {
      const attrs = node.attrs ?? {};
      return {
        ...node,
        attrs: {
          ...attrs,
          tableId: newTableId,
          phaseTitle: investmentCopyTitle(String(attrs.phaseTitle ?? ''), existingTitles),
        },
      };
    }

    if (node.type === 'investmentTotalNode') {
      return {
        ...node,
        attrs: {
          ...(node.attrs ?? {}),
          tableId: newTableId,
        },
      };
    }

    if (node.content) return { ...node, ...(node.attrs?.investmentProduct ? { attrs: { ...node.attrs, investmentProduct: { ...node.attrs.investmentProduct, id: crypto.randomUUID() } } } : {}), content: cloneInvestmentPhaseNodes(node.content, newTableId, existingTitles) };
    if (node.attrs?.investmentProduct) return { ...node, attrs: { ...node.attrs, investmentProduct: { ...node.attrs.investmentProduct, id: crypto.randomUUID() } } };
    return node;
  });
}

/**
 * Bouwstenen voor een fase (header + tabel + totaalrij). Gedeeld door het
 * groepsoverzicht en de kaart onder elke tabel, die allebei een nieuw
 * investeringsoverzicht kunnen invoegen.
 */
export function buildNewPhaseNodes(phaseNumber: number, L: InvestmentTableLabelsResolved) {
  const tableId = `phase-${Date.now()}-${phaseNumber}`;
  const headerRow = buildInvestmentHeaderRowJson('fixed', L);
  return [
    {
      type: 'investmentHeaderNode',
      attrs: {
        mainTitle: '',
        isMainHeader: false,
        phaseTitle: `Investeringsoverzicht ${phaseNumber}`,
        phaseSubtitle: '',
        pricingType: 'fixed',
        tableId,
        clientLogoUrl: null,
        agencyLogoUrl: null,
      },
    },
    {
      type: 'table',
      content: [
        { ...headerRow, content: applyInvestmentColumnWidths(headerRow.content ?? [], 'fixed') },
        {
          type: 'tableRow',
          content: applyInvestmentColumnWidths(
            [{ type: 'tableCell', content: [{ type: 'paragraph' }] }],
            'fixed'
          ),
        },
      ],
    },
    {
      type: 'investmentTotalNode',
      attrs: {
        tableId,
        pricingType: 'fixed',
        label: L.totalRow,
        fixedTotal: 0,
        fixedTotalText: null,
        showTotalRow: true,
      },
    },
  ];
}

export function buildPhaseFromLibrary(
  saved: SavedInvestmentTable,
  currency: string,
  L: InvestmentTableLabelsResolved
) {
  const t = saved.table;
  const tableId = `phase-${Date.now()}-lib`;
  const pt = t.pricingType as PricingType;

  const headerRow = buildInvestmentHeaderRowJson(pt, L);
  const headerCells = (headerRow.content as any[]) || [];

  const dataRows = (t.rows || []).map((row: any) => {
    if (row.investmentProduct) return investmentProductRow({ ...row.investmentProduct, id: crypto.randomUUID() }, pt, currency);
    let cells: any[];
    if (pt === 'hourly') {
      const price = row.price ?? 0;
      const hours = row.hours ?? 0;
      const sub = price * hours;
      const amountText = row.useText ? (row.text ?? '') : formatCurrency(price, currency);
      const subtotalText =
        row.subtotalText?.trim() || (row.useText ? amountText : formatCurrency(sub, currency));
      cells = [
        { type: 'tableCell', content: investmentTitleCellContent(row) },
        {
          type: 'tableCell',
          content: [
            {
              type: 'paragraph',
              ...(amountText ? { content: [{ type: 'text', text: amountText }] } : {}),
            },
          ],
        },
        {
          type: 'tableCell',
          content: [
            {
              type: 'paragraph',
              ...(!row.useText && row.hours != null
                ? { content: [{ type: 'text', text: String(hours) }] }
                : {}),
            },
          ],
        },
        {
          type: 'tableCell',
          content: [
            {
              type: 'paragraph',
              ...(subtotalText ? { content: [{ type: 'text', text: subtotalText }] } : {}),
            },
          ],
        },
      ];
    } else if (pt === 'fixed') {
      cells = [{ type: 'tableCell', content: investmentTitleCellContent(row) }];
    } else {
      const amountText = row.useText
        ? (row.text ?? '')
        : formatCurrency(row.subtotal ?? 0, currency);
      cells = [
        { type: 'tableCell', content: investmentTitleCellContent(row) },
        {
          type: 'tableCell',
          content: [
            {
              type: 'paragraph',
              ...(amountText ? { content: [{ type: 'text', text: amountText }] } : {}),
            },
          ],
        },
      ];
    }
    return {
      type: 'tableRow',
      attrs: {
        ...(row.rateKey ? { rateKey: row.rateKey } : {}),
        ...(typeof row.showHours === 'boolean' ? { showHours: row.showHours } : {}),
      },
      content: applyInvestmentColumnWidths(cells, pt, t.columnWidths),
    };
  });

  if (dataRows.length === 0) {
    const colCount = pt === 'hourly' ? 4 : pt === 'fixed' ? 1 : 2;
    dataRows.push({
      type: 'tableRow',
      content: applyInvestmentColumnWidths(
        Array.from({ length: colCount }, () => ({
          type: 'tableCell',
          content: [{ type: 'paragraph' }],
        })),
        pt,
        t.columnWidths
      ),
    });
  }

  return [
    {
      type: 'investmentHeaderNode',
      attrs: {
        mainTitle: '',
        isMainHeader: false,
        phaseTitle: t.title || saved.name,
        phaseSubtitle: t.subtitle ?? '',
        pricingType: pt,
        tableId,
        clientLogoUrl: null,
        agencyLogoUrl: null,
        showHourColumns: t.showHourColumns !== false,
        showRowPrices: t.showRowPrices !== false,
      },
    },
    {
      type: 'table',
      content: [
        {
          type: 'tableRow',
          content: applyInvestmentColumnWidths(headerCells, pt, t.columnWidths),
        },
        ...dataRows,
      ],
    },
    {
      type: 'investmentTotalNode',
      attrs: {
        tableId,
        pricingType: pt,
        label: L.totalRow,
        fixedTotal: pt === 'fixed' ? (t.fixedTotal ?? 0) : 0,
        fixedTotalText: pt === 'fixed' ? (t.fixedTotalText ?? null) : null,
        showTotalRow: t.showTotalRow !== false,
      },
    },
  ];
}

/** Voegt direct achter het gekozen overzicht in, ook tussen bestaande overzichten. */
export function buildInvestmentOverviewAfter(
  doc: PMNode,
  totalPos: number,
  labels: InvestmentTableLabelsResolved
): { position: number; content: ReturnType<typeof buildNewPhaseNodes> } | null {
  const total = doc.nodeAt(totalPos);
  if (total?.type.name !== 'investmentTotalNode') return null;
  const group = doc.resolve(totalPos).parent;
  if (group.type.name !== 'investmentGroupNode') return null;
  let overviewCount = 0;
  group.forEach((child) => {
    if (child.type.name === 'investmentHeaderNode' && !child.attrs.isMainHeader) overviewCount++;
  });
  return {
    position: totalPos + total.nodeSize,
    content: buildNewPhaseNodes(overviewCount + 1, labels),
  };
}
